import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  DocumentInput,
  DocumentAttachmentInput,
  DocumentTagInput,
  LibraryFeatured,
  PublicDocument,
  PublicDocumentAttachment,
  PublicDocumentTag,
} from "@vedaunion/shared";
import { apiFetch, ApiError } from "./api";

/**
 * React Query hooks over /api/documents and /api/document-tags. Mirrors
 * `lib/articles.ts` so the editor pages can use a familiar shape.
 */

export interface DocumentListParams {
  status?: string;
  tag?: string; // "namespace:slug" or just "slug"
  pinned?: boolean;
  limit?: number;
  all?: boolean;
  mine?: boolean;
  q?: string;
}

const LIST_KEY = (p: DocumentListParams) => ["documents", "list", p] as const;
const DETAIL_KEY = (slug: string) => ["documents", "detail", slug] as const;
const FEATURED_KEY = ["documents", "featured"] as const;
const TAGS_KEY = (ns?: string) => ["document-tags", ns ?? "all"] as const;

function buildQuery(p: DocumentListParams): string {
  const sp = new URLSearchParams();
  if (p.status) sp.set("status", p.status);
  if (p.tag) sp.set("tag", p.tag);
  if (p.pinned) sp.set("pinned", "true");
  if (p.limit) sp.set("limit", String(p.limit));
  if (p.all) sp.set("all", "true");
  if (p.mine) sp.set("mine", "true");
  if (p.q) sp.set("q", p.q);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function useDocuments(params: DocumentListParams = {}) {
  return useQuery<{ documents: PublicDocument[] }>({
    queryKey: LIST_KEY(params),
    queryFn: ({ signal }) =>
      apiFetch<{ documents: PublicDocument[] }>(`/api/documents${buildQuery(params)}`, { signal }),
  });
}

export function useDocument(slug: string | undefined) {
  return useQuery<{ document: PublicDocument }>({
    queryKey: DETAIL_KEY(slug ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<{ document: PublicDocument }>(`/api/documents/${slug}`, { signal }),
    enabled: !!slug,
  });
}

export function useFeaturedLibrary() {
  return useQuery<LibraryFeatured>({
    queryKey: FEATURED_KEY,
    queryFn: ({ signal }) => apiFetch<LibraryFeatured>(`/api/documents/featured`, { signal }),
    staleTime: 30_000,
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation<{ document: PublicDocument }, ApiError, DocumentInput>({
    mutationFn: (body) =>
      apiFetch<{ document: PublicDocument }>("/api/documents", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useUpdateDocument(id: number) {
  const qc = useQueryClient();
  return useMutation<{ document: PublicDocument }, ApiError, Partial<DocumentInput>>({
    mutationFn: (body) =>
      apiFetch<{ document: PublicDocument }>(`/api/documents/${id}`, { method: "PATCH", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; deleted: number }, ApiError, number>({
    mutationFn: (id) =>
      apiFetch<{ ok: true; deleted: number }>(`/api/documents/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });
}

// -----------------------------------------------------------------------------
// Tags
// -----------------------------------------------------------------------------

export function useDocumentTags(namespace?: string) {
  return useQuery<{ tags: PublicDocumentTag[] }>({
    queryKey: TAGS_KEY(namespace),
    queryFn: ({ signal }) => {
      const qs = namespace ? `?namespace=${encodeURIComponent(namespace)}` : "";
      return apiFetch<{ tags: PublicDocumentTag[] }>(`/api/document-tags${qs}`, { signal });
    },
    staleTime: 60_000,
  });
}

export function useCreateDocumentTag() {
  const qc = useQueryClient();
  return useMutation<{ tag: PublicDocumentTag }, ApiError, DocumentTagInput>({
    mutationFn: (body) =>
      apiFetch<{ tag: PublicDocumentTag }>(`/api/document-tags`, { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["document-tags"] }),
  });
}

export function useUpdateDocumentTag(id: number) {
  const qc = useQueryClient();
  return useMutation<{ tag: PublicDocumentTag }, ApiError, Partial<DocumentTagInput>>({
    mutationFn: (body) =>
      apiFetch<{ tag: PublicDocumentTag }>(`/api/document-tags/${id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["document-tags"] }),
  });
}

export function useDeleteDocumentTag() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; deleted: number }, ApiError, number>({
    mutationFn: (id) =>
      apiFetch<{ ok: true; deleted: number }>(`/api/document-tags/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["document-tags"] }),
  });
}

// -----------------------------------------------------------------------------
// Attachments
// -----------------------------------------------------------------------------

export function useDocumentAttachments(documentId: number | null) {
  return useQuery<{ attachments: PublicDocumentAttachment[] }>({
    queryKey: ["documents", "attachments", documentId],
    queryFn: ({ signal }) =>
      apiFetch<{ attachments: PublicDocumentAttachment[] }>(
        `/api/documents/${documentId}/attachments`,
        { signal },
      ),
    enabled: documentId !== null && documentId > 0,
  });
}

export function useCreateAttachment(documentId: number) {
  const qc = useQueryClient();
  return useMutation<{ attachments: PublicDocumentAttachment[] }, ApiError, DocumentAttachmentInput>({
    mutationFn: (body) =>
      apiFetch<{ attachments: PublicDocumentAttachment[] }>(
        `/api/documents/${documentId}/attachments`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents", "attachments", documentId] });
      qc.invalidateQueries({ queryKey: ["documents", "detail"] });
    },
  });
}

export function useUpdateAttachment(documentId: number) {
  const qc = useQueryClient();
  return useMutation<
    { ok: true },
    ApiError,
    { id: number; body: Partial<DocumentAttachmentInput> }
  >({
    mutationFn: ({ id, body }) =>
      apiFetch<{ ok: true }>(`/api/documents/${documentId}/attachments/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents", "attachments", documentId] });
      qc.invalidateQueries({ queryKey: ["documents", "detail"] });
    },
  });
}

export function useDeleteAttachment(documentId: number) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, number>({
    mutationFn: (id) =>
      apiFetch<{ ok: true }>(`/api/documents/${documentId}/attachments/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents", "attachments", documentId] });
      qc.invalidateQueries({ queryKey: ["documents", "detail"] });
    },
  });
}

export function useReorderAttachments(documentId: number) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, number[]>({
    mutationFn: (orderedIds) =>
      apiFetch<{ ok: true }>(`/api/documents/${documentId}/attachments/order`, {
        method: "PATCH",
        body: { orderedIds },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents", "attachments", documentId] });
      qc.invalidateQueries({ queryKey: ["documents", "detail"] });
    },
  });
}

// -----------------------------------------------------------------------------
// Revisions
// -----------------------------------------------------------------------------

export interface DocumentRevisionRef {
  id: number;
  versionNumber: number;
  title: string;
  note: string | null;
  editorId: number | null;
  createdAt: string;
}

export function useDocumentRevisions(id: number | null) {
  return useQuery<{ revisions: DocumentRevisionRef[] }>({
    queryKey: ["documents", "revisions", id],
    queryFn: ({ signal }) =>
      apiFetch<{ revisions: DocumentRevisionRef[] }>(`/api/documents/${id}/revisions`, { signal }),
    enabled: id !== null && id > 0,
  });
}

// -----------------------------------------------------------------------------
// Event ↔ document linking (called from the event editor)
// -----------------------------------------------------------------------------

export function useSetEventDocuments(eventId: number) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, number[]>({
    mutationFn: (documentIds) =>
      apiFetch<{ ok: true }>(`/api/events/${eventId}/documents`, {
        method: "PUT",
        body: { documentIds },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}
